import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useReserveContext } from '../../../contexts/useReserveContext';
import { postCreateReservationByStaff } from '../../../utils/reservation/by-staff/postCreateReservationByStaff';

import ReserveStepHeader from '../ReserveStepHeader';
import ReserveSelectionSummary from '../ReserveSelectionSummary';
import ReserveStepFooter from '../ReserveStepFooter';
import { useI18n } from '../../../i18n';

const money = (n?: number | null) => (n == null ? '-' : `$${Number(n).toFixed(2)}`);

const addMinutesToHHmm = (start: string, minutes: number): string => {
  const [h, m] = start.split(':').map(Number);
  const total = h * 60 + m + minutes;
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

const ConfirmReservationByStaff: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const {
    storeId,
    storeName,
    selectedDate,
    selectedStaffId,
    selectedStaffName,
    selectedServices,
    selectedStartTime,
    setSelectedServices,
    setSelectedDate,
    setSelectedStartTime,
    setSelectedStaffId,
    setSelectedStaffName,
    setIsFreeReservation,
  } = useReserveContext();

  const [submitting, setSubmitting] = useState(false);
  const [hasConfirmed, setHasConfirmed] = useState(false);

  const customerName = localStorage.getItem('customerName') || t('account.guest');
  const customerId = Number(localStorage.getItem('customerId'));

  useEffect(() => {
    if (hasConfirmed) return;

    if (!storeId) {
      toast.info(t('date.selectStoreFirst'));
      navigate('/reserve/by-staff/store');
      return;
    }
    if (!selectedStaffId) {
      toast.info(t('date.selectStaffFirst'));
      navigate('/reserve/by-staff/staff');
      return;
    }
    if (selectedServices.length === 0) {
      toast.info(t('date.selectServiceFirst'));
      navigate('/reserve/by-staff/service');
      return;
    }
    if (!selectedDate) {
      toast.info(t('date.chooseTitle'));
      navigate('/reserve/by-staff/date');
      return;
    }
    if (!selectedStartTime) {
      toast.info(t('time.selectWarning'));
      navigate('/reserve/by-staff/time');
      return;
    }
  }, [
    storeId,
    selectedStaffId,
    selectedServices.length,
    selectedDate,
    selectedStartTime,
    hasConfirmed,
    navigate,
    t,
  ]);

  const totalMinutes = useMemo(
    () => selectedServices.reduce((sum, s: any) => sum + (Number(s.duration_minutes) || 0), 0),
    [selectedServices],
  );

  const totalPrice = useMemo(
    () => selectedServices.reduce((sum, s: any) => sum + (Number(s.price) || 0), 0),
    [selectedServices],
  );

  const endTime = useMemo(() => {
    if (!selectedStartTime) return null;
    return addMinutesToHHmm(selectedStartTime, totalMinutes);
  }, [selectedStartTime, totalMinutes]);

  const handleConfirm = async () => {
    if (submitting) return;

    if (!storeId || !selectedDate || !selectedStaffId || !selectedStartTime) {
      toast.error(t('service.incomplete'));
      return;
    }
    if (selectedServices.length === 0) {
      toast.warning(t('date.selectServiceFirst'));
      navigate('/reserve/by-staff/service');
      return;
    }

    setSubmitting(true);
    try {
      await postCreateReservationByStaff({
        storeId,
        customerId: Number.isFinite(customerId) && customerId > 0 ? customerId : null,
        customerName: customerName || t('account.guest'),
        date: selectedDate,
        staffId: selectedStaffId,
        startTime: selectedStartTime,
        services: selectedServices.map((s: any) => ({ service_id: s.serviceId })),
      });

      toast.success(t('confirm.success'));

      setHasConfirmed(true);
      navigate('/reserve', { replace: true });

      setSelectedServices([]);
      setSelectedDate(null);
      setSelectedStartTime(null);
      setSelectedStaffId(null);
      setSelectedStaffName(null);
      setIsFreeReservation(false);
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || t('confirm.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-[70vh] space-y-4">
      <ReserveStepHeader
        title={t('confirm.title')}
        mode="confirm"
        onBack={() => navigate('/reserve/by-staff/time')}
      />

      <section className="space-y-3 rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
        <div className="space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
            {t('confirm.summary')}
          </h2>
          <ReserveSelectionSummary
            customerName={customerName}
            storeName={storeName}
            date={selectedDate}
            startTime={selectedStartTime}
            endTime={endTime ?? null}
            staffName={selectedStaffName}
            variant="light"
            onSelectStore={() => navigate('/reserve/by-staff/store')}
            onSelectStaff={() => navigate('/reserve/by-staff/staff?edit=1')}
            onSelectDate={() => navigate('/reserve/by-staff/date?edit=1')}
            onSelectTime={() => navigate('/reserve/by-staff/time?edit=1')}
          />
        </div>

        <div className="h-px bg-slate-200" />

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('confirm.selectedServices')}
            </h2>
            <button
              type="button"
              onClick={() => navigate('/reserve/by-staff/service?edit=1')}
              className="rounded-full border border-slate-200 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-50"
            >
              {t('confirm.change')}
            </button>
          </div>

          <div className="space-y-2">
            {selectedServices.map((sv: any) => {
              const categoryName = sv.categoryName || '';
              const title = categoryName ? `${categoryName} • ${sv.serviceName}` : sv.serviceName;

              return (
                <div
                  key={sv.serviceId}
                  className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-slate-800">{title}</span>
                    <span className="text-[11px] text-slate-500">{sv.duration_minutes} min</span>
                  </div>
                  <span className="text-sm font-semibold text-slate-800">{money(sv.price)}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
          <div className="text-slate-600">
            {t('confirm.total')} <span className="font-semibold text-slate-800">{totalMinutes} min</span>
          </div>
          <div className="text-slate-600">
            {t('confirm.amount')} <span className="font-semibold text-slate-800">{money(totalPrice)}</span>
          </div>
        </div>
      </section>

      <ReserveStepFooter
        summary={t('confirm.timeSummary', {
          start: selectedStartTime ?? '',
          end: endTime ?? '--',
          minutes: totalMinutes,
          amount: money(totalPrice),
        })}
        primaryLabel={submitting ? t('confirm.confirming') : t('confirm.confirmReservation')}
        onPrimary={handleConfirm}
        primaryDisabled={submitting}
      />
    </div>
  );
};

export default ConfirmReservationByStaff;
